'use strict';

import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Status, TrackingConstants, fetchInitialState, filtersClosed, fetchResetFilterState } from './cfs-search-slice';
import { SearchType } from './search-filters/search-type.jsx';
import { LocationFilter } from './search-filters/location-filter.jsx';
import { PriceFilter } from './search-filters/price-filter.jsx';
import { FilterSummaryItem } from './search-filters/filter-summary-item.jsx';

import analytics from '../../../utils/analytics';

let StickyBottomExcerpt = ({ totalResultCount, onResetClick }) => {
	let text = '';
	if (totalResultCount >= 0) {
		text = `${totalResultCount?.toLocaleString('en-GB') ?? 'No'} results`;
	}

	return (
		<div className="search-filters__sticky-bottom">
			<div className="search-filters__sticky-bottom__button-container">
				<button className="search-filters__sticky-bottom__button button button--hollow" onClick={onResetClick}>Reset filters</button>
				<button className="search-filters__sticky-bottom__button button hide-for-xxlarge" data-toggle="offCanvasSearch">View results</button>
			</div>
			<div className="search-filters__result-count">
				<span>{text}</span>
			</div>
		</div>
	);
};
StickyBottomExcerpt = React.memo(StickyBottomExcerpt);

export const FilterSummary = ({ initialState, isNew }) => {
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(fetchInitialState(initialState));

		$(document).on('closed.zf.offCanvas', (event) => {
			if ($(event.target).is('#offCanvasSearch')) {
				dispatch(filtersClosed());
			}
		});
	}, []);

	const totalResultCount = useSelector((state) => state.cfsSearch.filterState.totalResultCount);
	const filterStatus = useSelector((state) => state.cfsSearch.filterStatus);

	let sectionClassName = 'search-filters';

	if (filterStatus === Status.Uninitialised || filterStatus === Status.Loading) {
		sectionClassName += ' search-filters--loading';
	}

	const filterData = {
		manufacturer: {
			name: 'manufacturer',
			label: 'Make',
			items: useSelector((state) => state.cfsSearch.filterState.manufacturerItems),
			navigated: useSelector((state) => state.cfsSearch.filterState.navigatedManufacturer),
			popularValues: [
				'Audi',
				'BMW',
				'Ford',
				'Kia',
				'Land Rover',
				'Mercedes-Benz',
				'Nissan',
				'Toyota',
				'Vauxhall',
				'Volkswagen'
			]
		},
		range: {
			name: 'range',
			label: 'Model', // Range displays as Model in UI
			items: useSelector((state) => state.cfsSearch.filterState.rangeItems),
			navigated: useSelector((state) => state.cfsSearch.filterState.navigatedRange)
		},
		model: {
			name: 'model',
			label: 'Variant', // Model displays as Variant in UI
			items: useSelector((state) => state.cfsSearch.filterState.modelItems),
			navigated: useSelector((state) => state.cfsSearch.filterState.navigatedModel)
		},
		trim: {
			name: 'trim',
			label: 'Trim',
			items: useSelector((state) => state.cfsSearch.filterState.trimItems),
			navigated: useSelector((state) => state.cfsSearch.filterState.navigatedTrim)
		},
		derivative: {
			name: 'derivative',
			label: 'Version', // Derivative displays as Version in UI
			items: useSelector((state) => state.cfsSearch.filterState.derivativeItems),
			navigated: useSelector((state) => state.cfsSearch.filterState.navigatedDerivative)
		},
		carType: {
			name: 'carType',
			label: 'Car type',
			items: useSelector((state) => state.cfsSearch.filterState.carTypeItems),
			navigated: useSelector((state) => state.cfsSearch.filterState.navigatedCarType)
		},
		gearbox: {
			name: 'gearbox',
			label: 'Gearbox',
			items: useSelector((state) => state.cfsSearch.filterState.gearboxItems),
			navigated: useSelector((state) => state.cfsSearch.filterState.navigatedGearbox)
		},
		fuelType: {
			name: 'fuelType',
			label: 'Fuel type',
			items: useSelector((state) => state.cfsSearch.filterState.fuelTypeItems),
			navigated: useSelector((state) => state.cfsSearch.filterState.navigatedFuelType)
		},
		mileage: {
			name: 'mileage',
			label: 'Mileage',
			items: useSelector((state) => state.cfsSearch.filterState.mileageItems),
			navigated: useSelector((state) => state.cfsSearch.filterState.navigatedMileage)
		},
		colour: {
			name: 'colour',
			label: 'Colour',
			items: useSelector((state) => state.cfsSearch.filterState.colourItems),
			navigated: useSelector((state) => state.cfsSearch.filterState.navigatedColour)
		},
		numberOfDoors: {
			name: 'numberOfDoors',
			label: 'Doors',
			items: useSelector((state) => state.cfsSearch.filterState.numberOfDoorsItems),
			navigated: useSelector((state) => state.cfsSearch.filterState.navigatedNumberOfDoors)
		},
		numberOfSeats: {
			name: 'numberOfSeats',
			label: 'Seats',
			items: useSelector((state) => state.cfsSearch.filterState.numberOfSeatsItems),
			navigated: useSelector((state) => state.cfsSearch.filterState.navigatedNumberOfSeats)
		},
		engineSize: {
			name: 'engineSize',
			label: 'Engine size',
			items: useSelector((state) => state.cfsSearch.filterState.engineSizeItems),
			navigated: useSelector((state) => state.cfsSearch.filterState.navigatedEngineSize)
		},
		year: {
			name: 'year',
			label: 'Years',
			items: useSelector((state) => state.cfsSearch.filterState.yearItems),
			navigated: useSelector((state) => state.cfsSearch.filterState.navigatedYear)
		},
	};

	const onResetClick = () => {
		analytics.trackEvent(TrackingConstants.Categories.Removed, 'Reset filters', TrackingConstants.Labels.Unset);
		dispatch(fetchResetFilterState());
	}

	let colourFilter, mileageFilter, yearFilter;
	if (!isNew) {
		colourFilter = (
			<FilterSummaryItem
				filterItems={filterData.colour.items}
				navigatedItem={filterData.colour.navigated}
				filterName={filterData.colour.name}
				label={filterData.colour.label}
			/>
		);

		mileageFilter = (
			<FilterSummaryItem
				filterItems={filterData.mileage.items}
				navigatedItem={filterData.mileage.navigated}
				filterName={filterData.mileage.name}
				label={filterData.mileage.label}
			/>
		);

		yearFilter = (
			<FilterSummaryItem
				filterItems={filterData.year.items}
				navigatedItem={filterData.year.navigated}
				filterName={filterData.year.name}
				label={filterData.year.label}
			/>
		);
	}

	return (
		<section className={sectionClassName}>
			<div className="search-filters__filters-container">
				<h2 className="search-filters__heading">
					<span className="search-filters__heading__icon icon-24x24-filters-blue" />
					<span>Filters</span>
				</h2>
				<button className="search-filters__close hide-for-xxlarge" data-toggle="offCanvasSearch">
					<span className="search-filters__close__icon icon-24x24-exit-blue" />
					<span>Save &amp; close</span>
				</button>

				<SearchType isNew={isNew} />

				<div className="search-filters__section">
					<h3 className="search-filters__section-heading">
						<span className="search-filters__section-heading__icon icon-24x24-location"></span>
						<span>Location</span>
					</h3>
					<LocationFilter />
				</div>

				<div className="search-filters__section">
					<h3 className="search-filters__section-heading">
						<span className="search-filters__section-heading__icon icon-24x24-car-details"></span>
						<span>Car details</span>
					</h3>
					<FilterSummaryItem
						filterItems={filterData.manufacturer.items}
						navigatedItem={filterData.manufacturer.navigated}
						filterName={filterData.manufacturer.name}
						label={filterData.manufacturer.label}
					/>
					<FilterSummaryItem
						filterItems={filterData.range.items}
						navigatedItem={filterData.range.navigated}
						filterName={filterData.range.name}
						label={filterData.range.label}
					/>
					<FilterSummaryItem
						filterItems={filterData.model.items}
						navigatedItem={filterData.model.navigated}
						filterName={filterData.model.name}
						label={filterData.model.label}
					/>
					<FilterSummaryItem
						filterItems={filterData.trim.items}
						navigatedItem={filterData.trim.navigated}
						filterName={filterData.trim.name}
						label={filterData.trim.label}
					/>
					<FilterSummaryItem
						filterItems={filterData.derivative.items}
						navigatedItem={filterData.derivative.navigated}
						filterName={filterData.derivative.name}
						label={filterData.derivative.label}
					/>
					<FilterSummaryItem
						filterItems={filterData.carType.items}
						navigatedItem={filterData.carType.navigated}
						filterName={filterData.carType.name}
						label={filterData.carType.label}
					/>
				</div>

				<div className="search-filters__section">
					<h3 className="search-filters__section-heading">
						<span className="search-filters__section-heading__icon icon-24x24-budget"></span>
						<span>Budget</span>
					</h3>
					<PriceFilter />
				</div>

				<div className="search-filters__section">
					<h3 className="search-filters__section-heading">
						<span className="search-filters__section-heading__icon icon-24x24-specs"></span>
						<span>Car specifications</span>
					</h3>
					{yearFilter}
					<FilterSummaryItem
						filterItems={filterData.gearbox.items}
						navigatedItem={filterData.gearbox.navigated}
						filterName={filterData.gearbox.name}
						label={filterData.gearbox.label}
					/>
					{mileageFilter}
					<FilterSummaryItem
						filterItems={filterData.fuelType.items}
						navigatedItem={filterData.fuelType.navigated}
						filterName={filterData.fuelType.name}
						label={filterData.fuelType.label}
					/>
					<FilterSummaryItem
						filterItems={filterData.engineSize.items}
						navigatedItem={filterData.engineSize.navigated}
						filterName={filterData.engineSize.name}
						label={filterData.engineSize.label}
					/>
					{colourFilter}
				</div>

				<div className="search-filters__section">
					<h3 className="search-filters__section-heading">
						<span className="search-filters__section-heading__icon icon-24x24-practicality"></span>
						<span>Practicality</span>
					</h3>
					<FilterSummaryItem
						filterItems={filterData.numberOfDoors.items}
						navigatedItem={filterData.numberOfDoors.navigated}
						filterName={filterData.numberOfDoors.name}
						label={filterData.numberOfDoors.label}
					/>
					<FilterSummaryItem
						filterItems={filterData.numberOfSeats.items}
						navigatedItem={filterData.numberOfSeats.navigated}
						filterName={filterData.numberOfSeats.name}
						label={filterData.numberOfSeats.label}
					/>
				</div>
			</div>

			<StickyBottomExcerpt totalResultCount={totalResultCount} onResetClick={onResetClick} />
		</section>
	);
}